import React, { useEffect, useState } from "react"
import { graphql, useStaticQuery, Link } from "gatsby"
import tw, { styled, theme } from "twin.macro"

import { brandBlock } from "utils/brand"
import Button from "components/button"
import PlanPrice from "components/plan-price"
import ParrotImg from "components/img"
import Logo from "images/logos/demand360-lite-product.png"
import LogoRegular from "images/logos/demand360-lite-product-regular.png"
import SectionBackground from "components/section-background"
import BgTopo from "images/bg/topo.jpg"
import BgDots from "images/bg/demand360-dots.jpg"

const Demand360AdContainer = styled.div`
  ${tw`font-body text-white overflow-hidden`}

  .header-wrapper {
    ${tw`mx-auto l:max-w-l font-bold relative`}

    picture.hero img,
    img.hero {
      ${tw`absolute`}

      width: 100%;
      top: 350px;

      @media (min-width: 400px) {
        top: 300px;
      }

      @media (min-width: 640px) {
        top: 0;
        width: auto;
        height: 755px;
        left: 50%;
      }

      @media (min-width: 1200px) {
        height: 970px;
      }
    }

    .header {
      ${tw`m:pl-8 m:text-left`}

      font-size: 15px;
      line-height: 1.6;
      text-align: center;

      height: 590px;

      @media (min-width: 400px) {
        height: 583px;
      }

      @media (min-width: 640px) {
        height: 450px;
      }

      @media (min-width: 1200px) {
        height: 500px;
        font-size: 18px;
      }

      .logo {
        width: 351px;

        @media (min-width: 640px) {
          width: 335px;
          margin-top: 30px;
        }

        @media (min-width: 1200px) {
          width: 488px;
        }
      }
    }
  }

  .centered-container {
    ${tw`l:max-w-l mx-auto relative z-10`}
  }

  .section {
    ${tw`flex flex-col l:flex-row`}
  }

  .plan-price {
    ${tw`h-24 l:h-32`}

    background: linear-gradient(180deg, #3e3d3c 0%, #191919 100%);
    box-shadow: 4px 4px 15px rgba(0, 0, 0, 0.1477);
    border-radius: 5px;

    .price {
      ${tw`text-springgreen-light font-body w-full m:w-7/12 font-bold`}
      max-width: auto;
      @media (min-width: 1200px) {
        max-width: 200px;
      }
    }
  }

  .report-copy {
    background: linear-gradient(180deg, #3c3b3a 0%, #191919 83.85%);
    @media (min-width: 1200px) {
      width: 408px;
    }

    h3 {
      ${tw`text-yellow text-2xl leading-normal l:text-3xl l:leading-normal font-bold mb-4 text-center l:text-left`}
    }

    ul {
      ${tw`font-body text-base text-left text-white list-disc ml-4`}

      strong {
        ${tw`text-springgreen-light`}
      }
    }
  }

  .report-cover {
    @media (min-width: 1200px) {
      width: 716px;
    }
  }
`

const StyledIcon = styled.img`
  ${(props) =>
    props.customCSS ||
    `
    max-width: 80px;
    max-height: 65px;
  `}
`

const BackgroundTop = styled.div`
  ${tw`pt-24 l:pt-32 pb-48`}

  &.home {
    padding-top: 300px;
  }

  border-bottom: 2px solid ${theme`colors.springgreen.light`};

  background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.51) 0%,
      rgba(0, 0, 0, 0) 75.56%
    )
    ${({ background }) => (background ? `, url(${background})` : "")};
  background-color: #110120;
  background-position: bottom;
  background-size: cover;
  background-repeat: no-repeat;
  position: relative;
`

const Demand360Ad = ({ className = "", gradientClass }) => {
  const [reportImage, setReportImage] = useState(null)

  const data = useStaticQuery(graphql`
    query {
      gcms {
        adContentBlocks: contentBlocks(
          where: { identifier: "demand360-ad" }
          first: 1
          orderBy: order_ASC
        ) {
          headerContent {
            html
          }
          mainContent {
            html
          }
          featuredImage {
            url(transformation: { image: { resize: { width: 800, fit: max } } })
            altText
          }
          callToAction
          linkUrl
        }
        highlightContentBlocks: contentBlocks(
          where: { identifier: "demand360-ad-highlights" }
          orderBy: order_ASC
        ) {
          id
          extraData
        }
        geoTargetContentBlocks: contentBlocks(
          where: { identifier_starts_with: "demand360-ad-geo-target" }
        ) {
          id
          featuredImage {
            url(transformation: { image: { resize: { width: 800, fit: max } } })
            altText
          }
          extraData
        }
        headerCta: contentBlocks(
          where: { identifier: "demand360-lite-header-cta" }
        ) {
          id
          featuredImage {
            url
          }
          headerContent {
            html
          }
          icon {
            url
          }
        }
      }
    }
  `)

  const { gcms } = data

  const adContent = brandBlock(gcms.adContentBlocks[0])
  const highlights = brandBlock(gcms.highlightContentBlocks[0])
  const headerCta = brandBlock(gcms.headerCta[0])
  const geoTargetImages = gcms.geoTargetContentBlocks.reduce(
    (prev, geoTarget) => {
      if (geoTarget.featuredImage && geoTarget.featuredImage.url) {
        prev[geoTarget.extraData?.code || "default"] =
          geoTarget.featuredImage.url
      }
      return prev
    },
    {}
  )

  useEffect(() => {
    let countryCode

    if (
      typeof window !== "undefined" &&
      window.country &&
      window.country.code !== null
    ) {
      countryCode = window.country.code
    }

    if (geoTargetImages[countryCode]) {
      setReportImage(geoTargetImages[countryCode])
    } else {
      setReportImage(geoTargetImages.default)
    }
  }, [geoTargetImages])

  if (data.gcms.adContentBlocks?.length !== 1) {
    return <></>
  }

  let bg, bgClassName, bgOpacity

  if (className.includes("measurement")) {
    bgClassName = "demand360-ad-measurement"
    bg = BgTopo
    bgOpacity = "1.0"
  } else {
    bgClassName = "demand360-ad"
    bg = BgDots
    bgOpacity = "0.15"
  }

  return (
    <Demand360AdContainer>
      <BackgroundTop background={headerCta?.icon?.url} className={className}>
        <div className="header-wrapper flex flex-col m:flex-row">
          <div className="header">
            <Link to="/products/demand360/lite/">
              <img className="logo mx-auto m:hidden l:hidden" src={Logo} />
              <img
                className="logo hidden m:inline-block l:inline-block"
                src={LogoRegular}
              />
            </Link>
            <div
              className="pt-8 pb-6 font-normal px-3 l:px-0 m:px-0 m:w-2/5 l:w-2/5"
              dangerouslySetInnerHTML={{
                __html: headerCta.headerContent?.html,
              }}
            />
            <div className="plan-price flex flex-grow w-4/5 mx-auto m:mx-0 l:mx-0 m:w-1/3 l:w-1/3">
              <PlanPrice divided={true} />
            </div>
          </div>
          {headerCta.featuredImage && (
            <Link to="/products/demand360/lite/">
              <ParrotImg className="hero" src={headerCta.featuredImage} />
            </Link>
          )}
        </div>
        <div className="centered-container">
          <div className="section mt-0 l:mt-8 justify-between items-center l:items-start">
            {highlights.extraData.highlights.map((highlight, i) => (
              <div
                className="flex flex-col items-center justify-start w-4/5 l:w-1/4 mx-6 mt-12 l:mt-0"
                key={i}
              >
                <div className="h-20 flex flex-col items-center justify-center mb-3">
                  <StyledIcon src={highlight.icon} />
                </div>
                <div
                  className="font-black text-white text-center text-lg px-4"
                  dangerouslySetInnerHTML={{
                    __html: highlight.text,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </BackgroundTop>
      <SectionBackground
        bg={bg}
        color="#0c0c0c"
        opacity={bgOpacity}
        className={`${bgClassName} ${className || ""}`}
        gradientClass={gradientClass}
      >
        <div className="centered-container pb-8">
          <div className="flex flex-col-reverse l:flex-row justify-center l:items-stretch w-11/12 l:w-auto mx-auto l:px-8">
            <div
              className="report-copy l:-mt-24 p-8 z-0 rounded-b-lg l:rounded-b-none l:rounded-l-lg font-display"
              dangerouslySetInnerHTML={{ __html: adContent.mainContent?.html }}
            ></div>
            <div className="report-cover -mt-24 z-0">
              {reportImage && (
                <img
                  src={reportImage}
                  className="w-full object-cover rounded-t-lg l:rounded-t-none l:rounded-r-lg"
                />
              )}
            </div>
          </div>
          <div className="section mt-16 mb-16 l:mb-8 pb-16 items-center justify-center mx-auto">
            <Button to={adContent.linkUrl}>{adContent.callToAction}</Button>
          </div>
        </div>
      </SectionBackground>
    </Demand360AdContainer>
  )
}

export default Demand360Ad
